import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import {
  AccountAutoComplete,
  OptionSetsDropdown,
  SitesAutoComplete,
  StorageLocationAutoComplete,
} from '@/components';
import { AdvancedDonorLookup } from '@/components/common/advanced-donor-lookup';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import { BreedsAutoComplete } from '@/components/common/breeds-auto-complete';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { StorageLocationCombobox } from '@/components/common/storage-location-combobox';
import {
  EXPORT_ENUMS,
  FRESH_FROZEN,
  SEX_EMBRYO,
  STATE_CODE_MAP,
  STATUS_CODE_MAP,
} from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { EMBRYO_STATUS_MAP } from '@/pages/fresh-straw/constants';
import { Embryo, Site, StorageLocation } from '@/rtk-query.gql';

import {
  EditEmbryoFormInput,
  editEmbryoFormSchema,
  EditEmbryoFormValues,
} from './schema';

type EditEmbryoFormProps = {
  visible?: boolean;
  onCancel: () => void;
  onSubmit: (data: EditEmbryoFormValues) => void;
  embryo?: Embryo;
  readOnly?: boolean;
  readOnlyReason?: string;
};

const embryoStatusOptions = Object.entries(EMBRYO_STATUS_MAP).map(
  ([value, label]) => ({
    label,
    value: parseInt(value, 10),
  }),
);

const stateCodeOptions = Object.entries(STATE_CODE_MAP).map(
  ([value, label]) => ({
    label,
    value: parseInt(value, 10),
  }),
);

const statusCodeOptions = Object.entries(STATUS_CODE_MAP).map(
  ([value, label]) => ({
    label,
    value: parseInt(value, 10),
  }),
);

const sexOptions = Object.entries(SEX_EMBRYO).map(([label, value]) => ({
  label,
  value,
}));

const exportOptions = Object.entries(EXPORT_ENUMS).map(([label, value]) => ({
  label,
  value,
}));

const freshFrozenOptions = Object.entries(FRESH_FROZEN).map(
  ([label, value]) => ({
    label,
    value,
  }),
);

export const EditEmbryoForm = ({
  visible = true,
  onCancel,
  onSubmit,
  embryo,
  readOnly = false,
  readOnlyReason,
}: EditEmbryoFormProps) => {
  const { t } = useTranslation();
  const isStorageLocationPagingEnabled = useFeatureFlag(
    'Storage_Location_Paging',
  );
  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });

  const techsOptions = (techsData || []).map((tech) => ({
    label: tech.name,
    value: tech.id,
  }));

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditEmbryoFormInput>({
    resolver: zodResolver(editEmbryoFormSchema),
    disabled: readOnly,
    defaultValues: {
      embryoId: embryo?.id || '',
      status: undefined,
      freshFrozen: undefined,
      ownerId: undefined,
      soldTo: undefined,
      strawDisclaimer: undefined,
      strawNumber: undefined,
      cane: undefined,
      stage: undefined,
      grade: undefined,
      storageLocationId: undefined,
      siteId: undefined,
      shipmentId: undefined,
      creation: undefined,
      stateCode: undefined,
      statusCode: undefined,
      lastAct: undefined,
    } as EditEmbryoFormInput,
  });

  useEffect(() => {
    if (visible && embryo) {
      const embryoData = embryo as any;
      reset({
        embryoId: embryo?.id || embryoData?.embryoId || '',
        status: embryoData?.status || undefined,
        freshFrozen: embryoData?.freshFrozen || undefined,
        ownerId: embryoData?.ownerId || undefined,
        owner: embryoData?.owner
          ? {
              id: embryoData.owner.id,
              name: embryoData.owner.name,
              code: embryoData.owner.code || '',
            }
          : embryoData?.ownerId
            ? {
                id: embryoData.ownerId,
                name: '',
                code: '',
              }
            : undefined,
        soldTo: embryoData?.soldTo || undefined,
        strawDisclaimer: embryoData?.strawDisclaimer || undefined,
        strawNumber: embryoData?.strawNumber || undefined,
        cane: embryoData?.cane || undefined,
        stage: embryoData?.stage || undefined,
        grade: embryoData?.grade || undefined,
        storageLocationId: embryoData?.storageLocationId || undefined,
        storageLocation:
          embryoData?.storageLocationId && embryoData?.storageLocationName
            ? {
                id: embryoData.storageLocationId,
                name: embryoData.storageLocationName,
              }
            : undefined,
        siteId: embryoData?.siteId || undefined,
        site:
          embryoData?.siteId && embryoData?.site?.name
            ? ({
                id: embryoData.siteId,
                name: embryoData.site.name,
              } as Site)
            : undefined,
        shipmentId: embryoData?.shipmentId || undefined,
        shipment:
          embryoData?.shipmentId && embryoData?.shipmentName
            ? {
                id: embryoData.shipmentId,
                name: embryoData.shipmentName,
              }
            : undefined,
        creation: embryoData?.creation
          ? new Date(embryoData.creation)
          : undefined,
        stateCode: embryoData?.stateCode || undefined,
        statusCode: embryoData?.statusCode || undefined,
        lastAct: embryoData?.lastAct || undefined,
        donorId: embryoData?.donorId || undefined,
        donor: embryoData?.donor
          ? {
              id: embryoData.donor.id,
              name: embryoData.donor.name,
            }
          : undefined,
        sireId: embryoData?.sireId || undefined,
        sire: embryoData?.sire
          ? ({
              id: embryoData.sire.id,
              name: embryoData.sire.name,
            } as any)
          : undefined,
        embTag: embryoData?.embTag || undefined,
        sex: embryoData?.sex || undefined,
        breedId: embryoData?.breedId || undefined,
        breed:
          embryoData?.breedId && embryoData?.breedName
            ? {
                id: embryoData.breedId,
                name: embryoData.breedName,
              }
            : undefined,
        freshStrawNo: embryoData?.freshStrawNo || undefined,
        freezeMethodId: embryoData?.freezeMethodId || undefined,
        freezeMethod: embryoData?.freezeMethodId || undefined,
        freezer: embryoData?.freezer || undefined,
        dtPlus: embryoData?.dtPlus || undefined,
        tt: embryoData?.tt || undefined,
        freezeComment: embryoData?.freezeComment || undefined,
        export: embryoData?.export || undefined,
        freezeTechId: embryoData?.freezeTechId || undefined,
        freezeTech: embryoData?.freezeTechId || undefined,
        recoveryFirmCode: embryoData?.recoveryFirmCode || undefined,
        freezeTime: embryoData?.freezeTime || undefined,
        receivedOn: embryoData?.receivedOn
          ? new Date(embryoData.receivedOn)
          : undefined,
        grader7Id: embryoData?.grader7Id || undefined,
        grader7: embryoData?.grader7Id || undefined,
        crackedZona: embryoData?.crackedZona || undefined,
      } as EditEmbryoFormInput);
    }
  }, [visible, embryo, reset]);

  const getFormErrorMessage = (name: string) => {
    return (
      errors[name as keyof EditEmbryoFormInput] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name as keyof EditEmbryoFormInput]?.message}
        </small>
      )
    );
  };

  const handleFormSubmit = (data: EditEmbryoFormInput) => {
    const transformedData = editEmbryoFormSchema.parse(data);
    const apiInput = {
      embryoId: transformedData.embryoId,
      status: transformedData.status,
      freshFrozen: transformedData.freshFrozen,
      ownerId: transformedData.owner,
      soldTo: transformedData.soldTo,
      strawDisclaimer: transformedData.strawDisclaimer,
      strawNumber: transformedData.strawNumber,
      cane: transformedData.cane,
      stage: transformedData.stage,
      grade: transformedData.grade,
      storageLocationId: transformedData.storageLocation,
      siteId: transformedData.site,
      shipmentId: transformedData.shipment,
      creation: transformedData.creation,
      stateCode: transformedData.stateCode,
      statusCode: transformedData.statusCode,
      lastAct: transformedData.lastAct,
      donorId: transformedData.donor,
      sireId: transformedData.sire,
      embTag: transformedData.embTag,
      sex: transformedData.sex,
      breedId: transformedData.breed,
      freshStrawNo: transformedData.freshStrawNo,
      freezeMethodId: transformedData.freezeMethod,
      freezer: transformedData.freezer,
      dtPlus: transformedData.dtPlus,
      tt: transformedData.tt,
      freezeComment: transformedData.freezeComment,
      export: transformedData.export,
      freezeTechId: transformedData.freezeTech,
      recoveryFirmCode: transformedData.recoveryFirmCode,
      freezeTime: transformedData.freezeTime,
      receivedOn: transformedData.receivedOn,
      grader7Id: transformedData.grader7,
      crackedZona: transformedData.crackedZona,
    };
    onSubmit(apiInput as EditEmbryoFormValues);
    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit as any)}
      className="bg-white p-6"
    >
      {readOnly && readOnlyReason && (
        <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
          <span>{readOnlyReason}</span>
        </div>
      )}
      <div className="mb-8">
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h4 className="text-lg font-bold text-blue-600">
                {t('common.general')}
              </h4>
            </div>
            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.embryoStatus')}
              </label>
              <div className="w-48">
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={embryoStatusOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder="Select status"
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('status')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.freshFrozen')}
              </label>
              <div className="w-48">
                <Controller
                  name="freshFrozen"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={freshFrozenOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder={t('common.selectType')}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('freshFrozen')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.embryoOwner')}
              </label>
              <div className="w-48">
                <Controller
                  name="owner"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AccountAutoComplete
                      disabled={field.disabled}
                      id={field.name}
                      key={field.value?.id || 'owner'}
                      defaultValue={
                        field.value && field.value.name
                          ? ({
                              id: field.value.id,
                              name: field.value.name,
                              code: (field.value as any).code || '',
                            } as any)
                          : undefined
                      }
                      defaultId={
                        field.value?.id && !field.value.name
                          ? field.value.id
                          : undefined
                      }
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value);
                      }}
                    />
                  )}
                />
                {getFormErrorMessage('ownerId')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.soldTo')}
              </label>
              <div className="w-48">
                <Controller
                  name="soldTo"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('soldTo')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('straw.strawDisclaimer')}
              </label>
              <div className="w-48">
                <Controller
                  name="strawDisclaimer"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('strawDisclaimer')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('straw.straw')}
              </label>
              <div className="w-48">
                <Controller
                  name="strawNumber"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputNumber
                      disabled={field.disabled}
                      showButtons
                      value={field.value ?? null}
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      className={classNames('text-sm', 'w-full', {
                        'p-invalid': fieldState.invalid,
                      })}
                      inputClassName="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('strawNumber')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('donor.donor')}
              </label>
              <div className="w-48">
                <Controller
                  name="donor"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AdvancedDonorLookup
                      disabled={field.disabled}
                      label={undefined}
                      value={(field.value as any) ?? undefined}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(donor) => field.onChange(donor ?? undefined)}
                    />
                  )}
                />
                {getFormErrorMessage('donorId')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('sire.sire')}
              </label>
              <div className="w-48">
                <Controller
                  name="sire"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AdvancedSireLookup
                      disabled={field.disabled}
                      value={(field.value as any) ?? undefined}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(sire) => field.onChange(sire ?? undefined)}
                      showAddButton={false}
                    />
                  )}
                />
                {getFormErrorMessage('sireId')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.embryoTag')}
              </label>
              <div className="w-48">
                <Controller
                  name="embTag"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('embTag')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.sex')}
              </label>
              <div className="w-48">
                <Controller
                  name="sex"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={sexOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder={t('embryo.selectSex')}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('sex')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('fields.breed')}
              </label>
              <div className="w-48">
                <Controller
                  name="breed"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BreedsAutoComplete
                      disabled={field.disabled}
                      defaultValue={field.value as any}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value);
                      }}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('breedId')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('straw.freshStrawNumber')}
              </label>
              <div className="w-48">
                <Controller
                  name="freshStrawNo"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputNumber
                      disabled={field.disabled}
                      showButtons
                      value={field.value ?? null}
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      className={classNames('text-sm', 'w-full', {
                        'p-invalid': fieldState.invalid,
                      })}
                      inputClassName="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('freshStrawNo')}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <h4 className="text-lg font-bold text-blue-600">
                {t('shipping.shippingData')}
              </h4>
            </div>
            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('shipping.shipment')}
              </label>
              <div className="w-48">
                <Controller
                  name="shipment"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      disabled={field.disabled}
                      value={field.value?.id || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          field.onChange({
                            id: e.target.value,
                            name: e.target.value,
                          });
                        } else {
                          field.onChange(undefined);
                        }
                      }}
                      placeholder={t('shipping.shipmentId')}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('shipmentId')}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <h4 className="text-lg font-bold text-blue-600">
                {t('common.additionalInfo')}
              </h4>
            </div>
            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('common.status')}
              </label>
              <div className="w-48">
                <Controller
                  name="statusCode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={statusCodeOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder="Select status"
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('statusCode')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('common.lastActivity')}
              </label>
              <div className="w-48">
                <Controller
                  name="lastAct"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('lastAct')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('common.stateCode')}
              </label>
              <div className="w-48">
                <Controller
                  name="stateCode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={stateCodeOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder={t('common.selectState')}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('stateCode')}
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h4 className="text-lg font-bold text-blue-600">
                {t('freeze.embryoFreezing')}
              </h4>
            </div>
            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.freezeTech')}
              </label>
              <div className="w-48">
                <Controller
                  name="freezeTech"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={techsOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder={t('freeze.selectFreezeTech')}
                      filter
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('freezeTechId')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.recoveryFirmCode')}
              </label>
              <div className="w-48">
                <Controller
                  name="recoveryFirmCode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('recoveryFirmCode')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('semen.cane')}
              </label>
              <div className="w-48">
                <Controller
                  name="cane"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('cane')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.stage')}
              </label>
              <div className="w-48">
                <Controller
                  name="stage"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputNumber
                      disabled={field.disabled}
                      showButtons
                      value={field.value ?? null}
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      className={classNames('text-sm', 'w-full', {
                        'p-invalid': fieldState.invalid,
                      })}
                      inputClassName="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('stage')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.grade')}
              </label>
              <div className="w-48">
                <Controller
                  name="grade"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputNumber
                      disabled={field.disabled}
                      showButtons
                      value={field.value ?? null}
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      className={classNames('text-sm', 'w-full', {
                        'p-invalid': fieldState.invalid,
                      })}
                      inputClassName="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('grade')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.freezeTime')}
              </label>
              <div className="w-48">
                <Controller
                  name="freezeTime"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('freezeTime')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.freezeMethod')}
              </label>
              <div className="w-48">
                <Controller
                  name="freezeMethod"
                  control={control}
                  render={({ field, fieldState }) => (
                    <OptionSetsDropdown
                      disabled={field.disabled}
                      optionSetsName="Freezing Methods"
                      value={field.value ?? null}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder={t('freeze.selectFreezeMethod')}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('freezeMethodId')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('storageLocation.storageLocation')}
              </label>
              <div className="w-48">
                <Controller
                  name="storageLocation"
                  control={control}
                  render={({ field, fieldState }) =>
                    isStorageLocationPagingEnabled ? (
                      <StorageLocationCombobox
                        value={(field.value as StorageLocation) || null}
                        onChange={field.onChange}
                        disabled={field.disabled}
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        pagingEnabled
                      />
                    ) : (
                      <StorageLocationAutoComplete
                        disabled={field.disabled}
                        label={undefined}
                        defaultValue={
                          field.value as StorageLocation | undefined
                        }
                        onSelect={(e: AutoCompleteSelectEvent) => {
                          field.onChange(e.value);
                        }}
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                      />
                    )
                  }
                />
                {getFormErrorMessage('storageLocationId')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.freezer')}
              </label>
              <div className="w-48">
                <Controller
                  name="freezer"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('freezer')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.dtPlus')}
              </label>
              <div className="w-48">
                <Controller
                  name="dtPlus"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      disabled={field.disabled}
                      inputId="dtPlus"
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.checked ?? false)}
                      className={classNames({
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('dtPlus')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.creationFreezeDate')}
              </label>
              <div className="w-48">
                <Controller
                  name="creation"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Calendar
                      disabled={field.disabled}
                      value={field.value ?? undefined}
                      onChange={(e) => {
                        const value = e.value as Date | null | undefined;
                        field.onChange(value ?? undefined);
                      }}
                      showIcon
                      dateFormat="mm-dd-yy"
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('creation')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('common.received')}
              </label>
              <div className="w-48">
                <Controller
                  name="receivedOn"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Calendar
                      disabled={field.disabled}
                      value={field.value ?? undefined}
                      onChange={(e) => {
                        const value = e.value as Date | null | undefined;
                        field.onChange(value ?? undefined);
                      }}
                      showIcon
                      dateFormat="mm-dd-yy"
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('receivedOn')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.trypsinTreated')}
              </label>
              <div className="w-48">
                <Controller
                  name="tt"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      disabled={field.disabled}
                      inputId="tt"
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.checked ?? false)}
                      className={classNames({
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('tt')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.grader')}
              </label>
              <div className="w-48">
                <Controller
                  name="grader7"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={techsOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder={t('freeze.selectGrader')}
                      filter
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('grader7Id')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('freeze.freezeComment')}
              </label>
              <div className="w-48">
                <Controller
                  name="freezeComment"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      {...field}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('freezeComment')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('embryo.crackedZona')}
              </label>
              <div className="w-48">
                <Controller
                  name="crackedZona"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      disabled={field.disabled}
                      inputId="crackedZona"
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.checked ?? false)}
                      className={classNames({
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('crackedZona')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('shipping.export')}
              </label>
              <div className="w-48">
                <Controller
                  name="export"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={field.disabled}
                      value={field.value ?? null}
                      options={exportOptions}
                      onChange={(e) => field.onChange(e.value ?? undefined)}
                      placeholder={t('shipping.selectExport')}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('export')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700">
                {t('site.siteId')}
              </label>
              <div className="w-48">
                <Controller
                  name="site"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SitesAutoComplete
                      disabled={field.disabled}
                      defaultValueId={field.value?.id}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value ?? undefined);
                      }}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage('siteId')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-6">
        <Button
          type="button"
          label={t('common.cancel')}
          icon="pi pi-times"
          severity="secondary"
          onClick={handleCancel}
        />
        <Button
          type="submit"
          label={t('common.save')}
          icon="pi pi-check"
          loading={isSubmitting}
          disabled={readOnly}
        />
      </div>
    </form>
  );
};
